import { localDate } from './campaigns'
import type { Task } from './types'

/**
 * Pure — no Supabase import, deliberately, so this is testable the way
 * `campaigns.ts`'s `localDate` and `milestones.ts` are and `outbox.ts`'s
 * `nextHandedOff` is: anything that touches `supabase.ts` pulls in
 * `import.meta.env`, which this test harness's plain-Node runner has no
 * value for, and the whole module fails to import at all. `tasks.ts` holds
 * the actual reads/writes and imports these back for the components to use
 * alongside them.
 */

export type TaskSortKey = 'due' | 'agent' | 'done'

export const TASK_SORTS: { key: TaskSortKey; label: string }[] = [
  { key: 'due', label: 'Due date' },
  { key: 'agent', label: 'Agent' },
  { key: 'done', label: 'Done' },
]

/**
 * The Tasks tab's own sort. `agentName` is injected rather than looked up
 * here because this file has no roster of its own to resolve an id against
 * — the caller (which has one already, via `useRoster`) supplies it.
 *
 * Every sort puts open tasks before done ones first — "what's still mine to
 * do" is the question this tab answers, and a finished item sinking to the
 * bottom regardless of which axis she chose is what makes any of the three
 * choices still readable once a few dozen tasks are checked off.
 */
export function sortTasks(
  tasks: Task[],
  key: TaskSortKey,
  agentName: (agentId: string) => string,
): Task[] {
  const arr = [...tasks]
  const byOpenFirst = (a: Task, b: Task) => Number(a.done) - Number(b.done)
  switch (key) {
    case 'due':
      arr.sort((a, b) => {
        const open = byOpenFirst(a, b)
        if (open !== 0) return open
        // Undated sorts after every dated one, on both ends of the sort —
        // it's never "soonest" and it's never something a due-date read of
        // the list should surface first either.
        if (a.due_date === b.due_date) return 0
        if (a.due_date === null) return 1
        if (b.due_date === null) return -1
        return a.due_date < b.due_date ? -1 : 1
      })
      break
    case 'agent':
      arr.sort((a, b) => {
        const open = byOpenFirst(a, b)
        if (open !== 0) return open
        return agentName(a.agent_id).localeCompare(agentName(b.agent_id))
      })
      break
    case 'done':
      // Done is the sort key itself here — no secondary tiebreak needed
      // beyond it, since within each group every row already reads as one
      // undifferentiated bucket ("still open" / "done").
      arr.sort(byOpenFirst)
      break
  }
  return arr
}

/** Not done, and due strictly before today — never true for a dateless task. */
export function isOverdue(t: Task, today = localDate(new Date())): boolean {
  return !t.done && t.due_date !== null && t.due_date < today
}

/** Not done, and due today. */
export function isDueToday(t: Task, today = localDate(new Date())): boolean {
  return !t.done && t.due_date === today
}
